import { useMemo } from 'react';

import { FieldSyncRule } from '@/modules/projects/components/modals/resolve-conflicts-modal';

const CAPACITY_FIELDS = ['capacityPerDay', 'billableCapacity', 'corporateFunctionCapacity'];

/**
 * Returns field sync rules for the team member capacity conflict resolution modal.
 *
 * Rules:
 * 1. isOnDemandAvailability → clears capacity fields (shown as "-") when On Demand,
 *    restores original form values when switched back. Applies on mount so that the
 *    fields are already "-" when the modal opens with On Demand pre-selected.
 * 2. billableCapacity ↔ corporateFunctionCapacity two-way sync so they always sum to 100%.
 *    Uses setFieldValueIfChanged to avoid infinite loops (React useEffect is async, so
 *    a ref-based guard between effects is unreliable).
 *
 * @param getOriginalFormValues - Returns the current form values from the tab (used for restore).
 */
export const useTeamMemberCapacitySyncRules = (
  getOriginalFormValues: () => Record<string, unknown>
): FieldSyncRule[] => {
  return useMemo(
    (): FieldSyncRule[] => [
      {
        watchField: 'isOnDemandAvailability',
        // Fire on mount so capacity fields are immediately shown as "-" when
        // the modal opens with On Demand already set to true.
        applyOnMount: true,
        onValueChange: (isOnDemand, setFieldValue) => {
          if (isOnDemand) {
            // On Demand selected — capacity fields are irrelevant, show "-"
            CAPACITY_FIELDS.forEach(field => setFieldValue(field, null));
          }
        },
      },
      {
        watchField: 'isOnDemandAvailability',
        // This second rule handles the restore when switching OFF On Demand.
        // It does NOT use applyOnMount so it only fires on actual user changes,
        // never on the initial mount (which would re-queue stale float watcher entries).
        onValueChange: (isOnDemand, setFieldValue) => {
          if (isOnDemand === false) {
            const originalValues = getOriginalFormValues();
            CAPACITY_FIELDS.forEach(field => setFieldValue(field, originalValues[field] ?? null));
          }
        },
      },
      {
        watchField: 'billableCapacity',
        onValueChange: (billable, _setFieldValue, getFieldValue, setFieldValueIfChanged) => {
          const currentCorporate = getFieldValue('corporateFunctionCapacity');
          if (currentCorporate === undefined) return;
          if (getFieldValue('isOnDemandAvailability')) return;

          if (billable === null || billable === undefined) {
            setFieldValueIfChanged('corporateFunctionCapacity', null);
          } else {
            // Normalize to 5 decimal places before deriving — this ensures that a stale
            // rawWatched value like 27.083330000000004 produces the same derived result as
            // the clean stored value 27.08333, preventing the sync from re-firing forever.
            const normalizedBillable = parseFloat((billable as number).toFixed(5));
            const derived = parseFloat((100 - normalizedBillable).toFixed(5));
            setFieldValueIfChanged('corporateFunctionCapacity', derived);
          }
        },
      },
      {
        watchField: 'corporateFunctionCapacity',
        onValueChange: (corporate, _setFieldValue, getFieldValue, setFieldValueIfChanged) => {
          const currentBillable = getFieldValue('billableCapacity');
          if (currentBillable === undefined) return;
          if (getFieldValue('isOnDemandAvailability')) return;

          if (corporate === null || corporate === undefined) {
            setFieldValueIfChanged('billableCapacity', null);
          } else {
            // Normalize to 5 decimal places before deriving — same reasoning as billable rule above.
            const normalizedCorporate = parseFloat((corporate as number).toFixed(5));
            const derived = parseFloat((100 - normalizedCorporate).toFixed(5));
            setFieldValueIfChanged('billableCapacity', derived);
          }
        },
      },
    ],
    []
  );
};
